<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import { Editor } from "@tiptap/core";
  import StarterKit from "@tiptap/starter-kit";
  import Link from "@tiptap/extension-link";
  import Underline from "@tiptap/extension-underline";
  import TextAlign from "@tiptap/extension-text-align";
  import TextStyle from "@tiptap/extension-text-style";
  import Color from "@tiptap/extension-color";
  import Highlight from "@tiptap/extension-highlight";
  import Image from "@tiptap/extension-image";
  import Youtube from "@tiptap/extension-youtube";
  import Table from "@tiptap/extension-table";
  import TableRow from "@tiptap/extension-table-row";
  import TableCell from "@tiptap/extension-table-cell";
  import TableHeader from "@tiptap/extension-table-header";
  import Document from "@tiptap/extension-document";
  import Paragraph from "@tiptap/extension-paragraph";
  import Text from "@tiptap/extension-text";
  import Gapcursor from "@tiptap/extension-gapcursor";
  import Dropcursor from "@tiptap/extension-dropcursor";
  import History from "@tiptap/extension-history";
  import Placeholder from "@tiptap/extension-placeholder";
  import CharacterCount from "@tiptap/extension-character-count";
  import InvisibleCharacters, { SpaceCharacter, HardBreakNode } from "@tiptap-pro/extension-invisible-characters";
  import DragHandle from "@tiptap-pro/extension-drag-handle";
  import { PreTableTitleDiv } from "./extensions/PreTableTitleDiv";
  import { FireList } from "./extensions/FireList";
  import { GiftList } from "./extensions/GiftList";
  import { ProsList } from "./extensions/ProsList";
  import { ConsList } from "./extensions/ConsList";
  import { AttributesPreserver } from "./extensions/AttributesPreserver";
  import { EditorView, keymap, lineNumbers } from "@codemirror/view";
  import { EditorState } from "@codemirror/state";
  import { html } from "@codemirror/lang-html";
  import { oneDark } from "@codemirror/theme-one-dark";
  import { defaultKeymap } from "@codemirror/commands";
  import { indentUnit } from "@codemirror/language";
  import MenuBar from "./MenuBar.svelte";
  import CompactMenuBar from "./CompactMenuBar.svelte";
  import OrderedList from "@tiptap/extension-ordered-list";
  import BulletList from "@tiptap/extension-bullet-list";
  import ListItem from "@tiptap/extension-list-item";
  import HardBreak from "@tiptap/extension-hard-break";
  import Blockquote from "@tiptap/extension-blockquote";

  export let text: string = "";
  export let placeholder: string = "";
  export let compactMode: boolean = false;
  export let maxLength: number | null = null;

  let element: HTMLElement;
  let editor: Editor;
  let showHtml = false;
  let htmlContent = "";
  let codeEditor: EditorView;
  let codeEditorElement: HTMLElement;
  let isTableActive = false;
  let showFullMenu = !compactMode;
  let characterCount = 0;
  let wordCount = 0;

  const dispatch = createEventDispatcher();

  const updateCounts = () => {
    if (editor?.storage.characterCount) {
      characterCount = editor.storage.characterCount.characters();
      wordCount = editor.storage.characterCount.words();
    }
  };
  const forceRerender = () => {
    editor = editor;
  };

  const formatHtml = (html: string) => {
    const formatted = html
      .replace(/></g, ">\n<")
      .replace(/(<div[^>]*>|<\/div>|<p>|<\/p>|<span[^>]*>|<\/span>)/g, "$1\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line, _, array) => {
        let indent = 0;
        for (let i = 0; i < array.length; i++) {
          if (array[i] === line) break;
          if (array[i].startsWith("</")) indent--;
          if (!array[i].startsWith("</") && array[i].endsWith(">")) indent++;
        }
        return "  ".repeat(Math.max(0, indent)) + line;
      })
      .join("\n");
    return formatted;
  };

  const initCodeEditor = () => {
    if (codeEditor) {
      codeEditor.destroy();
    }

    codeEditor = new EditorView({
      state: EditorState.create({
        doc: htmlContent,
        extensions: [
          lineNumbers(),
          html(),
          oneDark,
          keymap.of(defaultKeymap),
          indentUnit.of("  "),
          EditorView.theme({
            "&": { height: "400px" },
            ".cm-scroller": { overflow: "auto" },
            ".cm-content": { whiteSpace: "pre-wrap" },
            ".cm-line": { padding: "0 3px" },
          }),
        ],
      }),
      parent: codeEditorElement,
    });
  };

  const initEditor = () => {
    if (editor) {
      editor.destroy();
    }

    const CustomTable = Table.extend({
      renderHTML({ HTMLAttributes }) {
        return [
          "div",
          { class: "table-container-wrapper", style: "false" },
          ["div", { class: "table-container" }, ["table", { ...HTMLAttributes, class: "table-element" }, 0]],
        ];
      },
    });

    editor = new Editor({
      element: element,
      extensions: [
        StarterKit.configure({
          dropcursor: false,
          gapcursor: false,
          document: false,
          paragraph: false,
          text: false,
          history: false,
        }),
        HardBreak,
        InvisibleCharacters.configure({
          visible: true,
          builders: [new HardBreakNode()], // Only show line breaks, exclude spaces and paragraphs
        }),
        History.configure({
          depth: 100,
          newGroupDelay: 500,
        }),
        Placeholder.configure({
          placeholder: () => {
            return placeholder || "Write something …";
          },
          showOnlyWhenEditable: true,
          showOnlyCurrent: true,
        }),
        Blockquote,
        Link.configure({
          openOnClick: false,
        }),
        OrderedList.configure({
          HTMLAttributes: {
            class: "list-decimal list-inside",
          },
        }),
        BulletList.configure({
          HTMLAttributes: {
            class: "list-disc list-inside",
          },
        }),
        FireList.configure({
          HTMLAttributes: {
            class: "list-image-fire list-inside",
          },
        }),
        GiftList.configure({
          HTMLAttributes: {
            class: "list-image-gift list-inside",
          },
        }),
        ListItem,
        Underline,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        TextStyle,
        Color,
        Highlight,
        Image.configure({
          inline: true,
        }),
        Youtube.configure({
          controls: true,
          nocookie: true,
        }),
        CustomTable.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        Document,
        Paragraph,
        Text,
        Gapcursor,
        Dropcursor,
        CharacterCount.configure({
          limit: maxLength,
          mode: "textSize",
        }),
        Paragraph,
        DragHandle.configure({
          render: () => {
            const element = document.createElement("button");
            element.type = "button";
            element.innerHTML = `
                          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="w-5 h-5 iconify iconify--mdi" width="1em" height="1em" viewBox="0 0 24 24"><path fill="currentColor" d="M13 11h5l-1.5-1.5l1.42-1.42L21.84 12l-3.92 3.92l-1.42-1.42L18 13h-5v5l1.5-1.5l1.42 1.42L12 21.84l-3.92-3.92L9.5 16.5L11 18v-5H6l1.5 1.5l-1.42 1.42L2.16 12l3.92-3.92L7.5 9.5L6 11h5V6L9.5 7.5L8.08 6.08L12 2.16l3.92 3.92L14.5 7.5L13 6z"></path></svg>
                        `;
            element.className = "drag-handle";
            return element;
          },
          tippyOptions: {
            duration: [300, 200],
            placement: "left-start",
            arrow: true,
            animation: "shift-away",
          },
        }),
        PreTableTitleDiv,
        ProsList,
        ConsList,
        AttributesPreserver,
      ],
      content: text,
      parseOptions: {
        preserveWhitespace: "full",
      },
      onUpdate: ({ editor }) => {
        text = editor.getHTML();
        updateCounts();
        dispatch("change", { content: text });
        forceRerender();
      },
      onSelectionUpdate: ({ editor }) => {
        isTableActive = editor.isActive("table");
        forceRerender();
      },
      onTransaction: () => {
        forceRerender();
      },
      onCreate: ({ editor }) => {
        updateCounts();
      },
      editorProps: {
        attributes: {
          class: "prose prose-sm sm:prose-base lg:prose-lg xl:prose-xl focus:outline-none",
        },
      },
    });
  };

  const toggleHtml = () => {
    if (showHtml) {
      text = codeEditor.state.doc.toString();
      showHtml = false;
      setTimeout(() => {
        initEditor();
      }, 0);
    } else {
      htmlContent = formatHtml(editor.getHTML());
      showHtml = true;
      setTimeout(() => {
        initCodeEditor();
      }, 0);
    }
  };

  const toggleMenu = () => {
    showFullMenu = !showFullMenu;
  };

  onMount(() => {
    initEditor();

    // A custom solution to prevent links from opening when clicked in the editor - Default from TipTap Did not work
    const editorContent = element.querySelector(".ProseMirror");
    if (editorContent) {
      editorContent.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        if (target && target.tagName === "A") {
          e.preventDefault();
        }
      });
    }
  });

  onDestroy(() => {
    if (editor) {
      editor.destroy();
    }
    if (codeEditor) {
      codeEditor.destroy();
    }
  });
</script>

<div class="editor-wrapper">
  {#if editor}
    {#if compactMode}
      <CompactMenuBar {editor} on:toggleMenu={toggleMenu} />
    {:else}
      <MenuBar {editor} {compactMode} on:toggleMenu={toggleMenu} on:toggleHtml={toggleHtml} />
    {/if}
  {/if}

  {#if showHtml}
    <div class="html-editor" bind:this={codeEditorElement} />
  {:else}
    <div bind:this={element} class={compactMode ? "editor-content compact" : "editor-content"} />
  {/if}

  <div class={compactMode ? "editor-footer compact" : "editor-footer"}>
    <div class="character-count">
      {characterCount} characters
      {#if maxLength}
        <span class="limit-info">
          ({maxLength - characterCount} remaining)
        </span>
      {/if}
      · {wordCount} words
    </div>
  </div>
</div>

<style>
  .editor-wrapper {
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  :global(.prose) {
    max-width: 100% !important;
  }

  :global(.ProseMirror) {
    position: relative;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px;
    border: none !important; /*Keep the important to remove default border from TipTap editor*/
  }

  :global(.editor-content .drag-handle) {
    position: absolute;
    left: -24px;
    background: white !important;
    color: #747f8f !important;
    cursor: grab !important;
    padding: 4px !important;
    opacity: 1 !important;
    transition: opacity 0.2s !important;
    width: 30px !important;
    height: 30px !important;
  }

  :global(.editor-content .drag-handle:hover) {
    opacity: 1;
    color: #475569;
  }

  :global(.editor-content .drag-handle svg) {
    display: block;
    width: 100%;
    height: 100%;
  }

  :global(.tippy-box) {
    background: none;
    border: none;
    padding: 0;
  }

  :global(.tippy-arrow) {
    color: white;
  }

  .editor-content {
    font-weight: 300;
  }
  :global(.editor-content div[contenteditable="true"]) {
    font-size: 16px;
    padding-left: 32px;
  }

  :global(.editor-content) {
    /* padding-left: 32px; */
    max-width: 65ch;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.75;
    font-size: 1rem;
    background-color: white;
    max-height: 650px;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
  }

  :global(.ProseMirror.editor-content) {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  @media (min-width: 640px) {
    :global(.editor-content) {
      font-size: 0.875rem;
    }
  }

  @media (min-width: 1024px) {
    :global(.editor-content) {
      font-size: 1.125rem;
    }
  }

  .editor-content.compact {
    padding-left: 0;
    max-width: none;
    margin-left: 0;
    margin-right: 0;
    line-height: 1;
    background-color: transparent;
    max-height: none;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
  }

  :global(.editor-content.compact .ProseMirror) {
    border: none;
    border-radius: none;
    font-size: 14px;
  }

  @media (min-width: 1280px) {
    :global(.editor-content) {
      font-size: 1.25rem;
    }
  }

  :global(.editor-content:focus, .ProseMirror:focus) {
    outline: none;
  }

  :global(.cm-editor) {
    height: 100%;
  }

  :global(.cm-editor .cm-scroller) {
    font-family: "Fira Code", monospace;
    font-size: 14px;
    line-height: 1.5;
  }

  :global(div[data-youtube-video] iframe) {
    border: none;
  }

  :global(.tableWrapper table) {
    width: 100%;
    margin-bottom: 1rem;
    background-color: transparent;
    border-collapse: collapse !important;
    border-radius: 0px !important;
    border: 1px solid #e2e8f0 !important;
  }

  :global(.tableWrapper table th, .tableWrapper table td) {
    padding: 0.75rem;
    vertical-align: top;
    border: 1px solid #e2e8f0;
  }

  :global(.tableWrapper table thead th) {
    vertical-align: bottom;
    border-bottom: 1px solid #e2e8f0;
    background-color: #f8fafc;
    font-weight: 600;
    color: #475569;
  }

  :global(.tableWrapper table tbody + tbody) {
    border-top: 1px solid #e2e8f0;
  }

  :global(.tableWrapper table th, .tableWrapper table td) {
    border: 1px solid #e2e8f0;
    transition: background-color 0.2s ease;
  }

  :global(.tableWrapper table thead th, .tableWrapper table thead td) {
    border-bottom-width: 1px;
  }

  :global(.ProseMirror p.is-editor-empty:first-child::before) {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  :global(.ProseMirror h1.is-empty::before, .ProseMirror h2.is-empty::before, .ProseMirror h3.is-empty::before) {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .editor-footer {
    border-top: 1px solid #ccc;
    padding: 8px;
    font-size: 11px;
    color: #666;
    display: flex;
    justify-content: flex-end;
  }

  .editor-footer.compact {
    padding: 3px 8px;
    font-size: 10px;
  }

  .character-count {
    font-family: monospace;
    color: #2700ff;
  }

  .limit-info {
    color: #888;
  }

  :global(.ProseMirror.character-count--limited) {
    border-color: #ff6b6b;
  }

  :global(.pre-table-title) {
    background-color: #e54038;
    padding: 24px;
    border-radius: 8px 8px 0 0;
    font-size: 22px;
    font-weight: 700;
    color: white;
  }
</style>
